// src/ConsumerApp/hooks/useHomeBackExit.js

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Tracks the index of the initial entry into the SPA.
 * When the user is on the Home page and hits the browser's back button,
 * it bypasses internal history loops and exits to the external referrer.
 */
export function useHomeBackExit() {
  const location = useLocation();
  const initialHistoryIndexRef = useRef(null);

  useEffect(() => {
    // Capture the initial window.history state index upon first mounting the app
    if (initialHistoryIndexRef.current === null) {
      initialHistoryIndexRef.current = window.history.state?.idx ?? 0;
    }
  }, []);

  useEffect(() => {
    const isHomePage =
      location.pathname === '/consumer' ||
      location.pathname === '/consumer/' ||
      location.pathname === '/consumer/HomeScreen';

    if (!isHomePage) return;

    const handlePopState = (event) => {
      // Calculate how deep the user went inside the SPA relative to initial entry
      const currentIdx = event.state?.idx ?? 0;
      const initialIdx = initialHistoryIndexRef.current ?? 0;

      // If user went back from Home and was at or beyond initial entry,
      // force jump past all internal SPA history entries to the external page
      if (currentIdx < initialIdx) {
        // Already external
        return;
      }

      if (currentIdx >= initialIdx) {
        const deltaToExit = -(currentIdx - initialIdx + 1);
        if (deltaToExit < 0) {
          window.history.go(deltaToExit);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);
}